import { useSyncExternalStore } from 'react';
import { analyticsEnabled, getConsent, setConsent, subscribeConsent } from '../utils/analytics';
import { useT } from '../hooks/useT';
import { chip } from '../utils/ui';

export function useConsent() {
  return useSyncExternalStore(subscribeConsent, getConsent, () => 'unset' as const);
}

/**
 * Kleine, niet-blokkerende vraag om anonieme statistieken. Verschijnt alleen als analytics aanstaat.
 * Staat boven de kaartknoppen (locatieknop en melding van de reservekaart), niet erover.
 * `verberg`: wacht met vragen tot de uitleg gezien is en terwijl een museum open staat.
 */
export function AnalyticsConsent({ verberg = false }: { verberg?: boolean }) {
  const { t } = useT();
  const consent = useConsent();
  if (!analyticsEnabled() || consent !== 'unset' || verberg) return null;
  return (
    <div
      role="region"
      aria-label={t('analyticsTitle')}
      className="pointer-events-none fixed inset-x-0 bottom-36 z-[110] flex justify-center px-4"
    >
      <div className="pointer-events-auto w-full max-w-xl space-y-3 rounded-2xl bg-slate-900/95 p-4 text-sm text-slate-50 backdrop-blur-md dark:bg-slate-100/95 dark:text-slate-900">
        <p>{t('analyticsAsk')}</p>
        <div className="flex gap-2">
          <button onClick={() => setConsent('granted')} className="min-h-11 whitespace-nowrap rounded-2xl bg-slate-50 px-4 font-semibold text-slate-900 transition-all duration-150 active:scale-95 dark:bg-slate-900 dark:text-slate-50">
            {t('analyticsAllow')}
          </button>
          <button onClick={() => setConsent('denied')} className="min-h-11 whitespace-nowrap rounded-2xl px-4 font-semibold transition-all duration-150 active:scale-95">
            {t('analyticsDeny')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Aan/uit in het menu, zodat een keuze altijd te wijzigen is. */
export function AnalyticsToggle() {
  const { t } = useT();
  const consent = useConsent();
  if (!analyticsEnabled()) return null;
  const on = consent === 'granted';
  return (
    <section className="space-y-3 border-t border-black/5 pt-6 dark:border-white/10">
      <h3 className="font-semibold">{t('analyticsTitle')}</h3>
      <p className="text-sm text-slate-600 dark:text-slate-400">{t('analyticsDesc')}</p>
      <button onClick={() => setConsent(on ? 'denied' : 'granted')} aria-pressed={on} className={chip(on)}>
        {on ? t('analyticsOn') : t('analyticsOff')}
      </button>
    </section>
  );
}
